import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

import iconCaretLeft from "../../../assets/media/icon-caret-left.svg";
import iconCaretRight from "../../../assets/media/icon-caret-right.svg";

function Pagination({
  setCurrentPage,
  currentPage = 1,
  totalPages,
  itemsPerPage = 10,
  setItemsPerPage,
  onItemsPerPageChange,
  totalItems = 0,
}) {
  const [_currentPage, _setCurrentPage] = useState(currentPage);
  const [_itemsPerPage, _setItemsPerPage] = useState((typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10);
  const [inputPage, setInputPage] = useState(currentPage);

  // Dynamic items per page options based on total items
  const getItemsPerPageOptions = () => {
    const baseOptions = [10, 20, 30, 40, 50];
    const extendedOptions = [60, 70, 80, 90, 100];

    // If we have less than 50 items, only show relevant options
    if (totalItems <= 50) {
      return baseOptions.filter(option => option <= totalItems);
    }

    // If we have less than 100 items, show base options + some extended
    if (totalItems <= 100) {
      return [...baseOptions, ...extendedOptions.filter(option => option <= totalItems)];
    }

    // For 100+ items, show all options
    return [...baseOptions, ...extendedOptions];
  };

  const itemsPerPageOptions = getItemsPerPageOptions();

  // Debug: Log pagination props

  // Sync internal state with props
  useEffect(() => {
    _setCurrentPage(currentPage);
    setInputPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;
    _setItemsPerPage(validItemsPerPage);
  }, [itemsPerPage]);

  const handlePageClick = (pageNumber) => {
    const pageNum = Number(pageNumber);
    const maxPages = typeof totalPages === 'function' ? totalPages() : totalPages;

    // Validate page number
    if (isNaN(pageNum) || pageNum < 1 || pageNum > maxPages) {
      // Reset input to current page if invalid
      setInputPage(_currentPage);
      return;
    }

    _setCurrentPage(pageNum);
    setInputPage(pageNum);
    setCurrentPage(pageNum);
  };

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    // Validate the new items per page value
    const validItemsPerPage = (typeof newItemsPerPage === 'number' && newItemsPerPage > 0) ? newItemsPerPage : 10;

    _setItemsPerPage(validItemsPerPage);

    // Reset to page 1 when changing items per page
    _setCurrentPage(1);
    setInputPage(1);
    setCurrentPage(1);

    // Call the parent handler if provided
    if (setItemsPerPage) {
      setItemsPerPage(validItemsPerPage);
    }
    if (onItemsPerPageChange) {
      onItemsPerPageChange(validItemsPerPage);
    }
  }, [setCurrentPage, setItemsPerPage, onItemsPerPageChange]);

  // Auto-adjust itemsPerPage if it's higher than total items
  useEffect(() => {
    if (totalItems > 0 && itemsPerPage > totalItems && itemsPerPageOptions.length > 0) {
      const maxValidOption = Math.max(...itemsPerPageOptions);
      if (maxValidOption > 0 && maxValidOption < itemsPerPage) {
        handleItemsPerPageChange(maxValidOption);
      }
    }
  }, [totalItems, itemsPerPage, itemsPerPageOptions, handleItemsPerPageChange]);

  // Generate page numbers with ellipsis for better layout
  const getPageNumbers = () => {
    const total = typeof totalPages === 'function' ? totalPages() : totalPages;
    const current = _currentPage;
    const delta = 2; // Number of pages to show on each side of current page
    const pages = [];

    if (total <= 7) {
      // Show all pages if total is 7 or less
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of middle section
      let start = Math.max(2, current - delta);
      let end = Math.min(total - 1, current + delta);

      // Adjust if we're near the start
      if (current <= delta + 2) {
        end = Math.min(5, total - 1);
      }

      // Adjust if we're near the end
      if (current >= total - delta - 1) {
        start = Math.max(2, total - 4);
      }

      // Add ellipsis after first page if needed
      if (start > 2) {
        pages.push('ellipsis-start');
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (end < total - 1) {
        pages.push('ellipsis-end');
      }

      // Always show last page
      if (total > 1) {
        pages.push(total);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="boardFooter">
      <div className="pagination">
        <Link
          className={`disabled ${_currentPage > 1 ? "active" : "hidden"}`}
          onClick={() => handlePageClick(_currentPage - 1)}
        >
          <img src={iconCaretLeft} alt="" />
        </Link>
        {pageNumbers.map((pageNum, index) => {
          if (pageNum === 'ellipsis-start' || pageNum === 'ellipsis-end') {
            return (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis" style={{ padding: '0 8px', cursor: 'default' }}>
                ...
              </span>
            );
          }
          return (
            <Link
              key={pageNum}
              className={`${_currentPage === pageNum ? "active" : ""}`}
              onClick={() => handlePageClick(pageNum)}
            >
              {pageNum}
            </Link>
          );
        })}
        <Link
          className={`disabled ${_currentPage < (typeof totalPages === 'function' ? totalPages() : totalPages) ? "active" : "hidden"
            }`}
          onClick={() => handlePageClick(_currentPage + 1)}
        >
          <img src={iconCaretRight} alt="" />
        </Link>

        {itemsPerPageOptions.length > 0 && (
          <select
            value={_itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="select-pagination"
          >
            {itemsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option} / page
              </option>
            ))}
          </select>
        )}
        <span>Goto</span>
        <input
          type="number"
          min={1}
          max={typeof totalPages === 'function' ? totalPages() : totalPages}
          value={inputPage}
          onFocus={(e) => e.target.select()}
          onMouseUp={(e) => e.preventDefault()}
          style={{
            width: '50px',
            padding: '0px',
            background: '#191b1c',
            color: 'white',
            borderRadius: '5px',
            textAlign: 'center',
            appearance: 'none',
            borderWidth: 'medium',
            borderStyle: 'none',
            borderColor: 'currentcolor',
            borderImage: 'none',
          }}
          placeholder="Page"
          onChange={(e) => {
            const value = e.target.value;
            setInputPage(value);
          }}
          onChangeCapture={(e) => {
            if (e.target.value) {
              handlePageClick(Number(e.target.value));
            }
          }}
          onBlur={(e) => {
            if (e.target.value) {
              handlePageClick(Number(e.target.value));
            }
          }}
        />
      </div>
    </div>
  );
}

export default Pagination;
